import { __decorate } from "tslib";
import { Component, Prop, Mixins } from 'vue-property-decorator';
import PageGreetingUpdate from '@/plugins/graphql/mutations/v2/page-greeting-update.gql';
import PageStoreTitleUpdate from '@/plugins/graphql/mutations/v2/page-store-title-update.gql';
import MiniAppNotificationsUpdate from '@/plugins/graphql/mutations/v2/mini-app-notification-update.gql';
import PageWelcomeMessageUpdate from '@/plugins/graphql/mutations/v2/welcome-message-update.gql';
import PersistentMenuUpdate from '@/plugins/graphql/mutations/v2/persistent-menu-update.gql';
import WelcomeImageUpdate from '@/plugins/graphql/mutations/v2/welcome-image-update.gql';
import AppUpdate from '@/plugins/graphql/mutations/v2/app-name-update.gql';
import { AppBasicModel } from '@/helpers/entities/models/AppBasicModel';
import { PageWelcomeMessageModel } from '@/helpers/entities/models/PageWelcomeMessageModel';
import { PageGreetingModel } from '@/helpers/entities/models/PageGreetingModel';
import { PageStoreTitleModel } from '@/helpers/entities/models/PageStoreTitleModel';
import AppBasic from '@/plugins/graphql/queries/v2/app-basic.gql';
import PageGreeting from '@/plugins/graphql/queries/v2/page-greeting.gql';
import PageStoreTitle from '@/plugins/graphql/queries/v2/page-store-title.gql';
import PageWelcomeMessage from '@/plugins/graphql/queries/v2/page-welcome-message.gql';
import MiniAppNotifications from '@/plugins/graphql/queries/v2/mini-app-notifications.gql';
import { DIY_DEFAULT_APP_NAMES } from '@/helpers/constants/app';
import { MiniAppNotificationModel } from '@/helpers/entities/models/MiniAppNotificationModel';
import { MiniAppNotificationInputModel } from '@/helpers/entities/models/MiniAppNotificationInputModel';
import { MALL_MANAGEMENT } from '@/helpers/constants/CustomBundleKeys';
import DiyMixin from './DiyMixin';
let PageSettingMixin = class PageSettingMixin extends Mixins(DiyMixin) {
    get appPayload() {
        const payload = { appUuid: null, appName: null };
        if (this.app) {
            let { name } = this.app;
            if (DIY_DEFAULT_APP_NAMES.includes(name)) {
                name = '';
            }
            return {
                appUuid: this.app.uuid,
                appName: name,
                shoulderConvenienceFee: this.app.shoulderConvenienceFee,
                shoulderProcessingFee: this.app.shoulderProcessingFee,
                ageRequirement: this.app.ageRequirement,
                generalAdsPostbackPayload: this.app.generalAdsPostbackPayload,
                referralCode: this.app.referrer?.affiliateCode,
                categoryTree: this.app.categoryTree,
                businessDescription: this.app.businessDescription,
                emailNotifRecipients: this.app.emailNotifRecipients
                    ? this.app.emailNotifRecipients.split(',')
                    : [],
                supportEmailList: this.app.supportEmailList ? this.app.supportEmailList.split(',') : [],
                supportContactNumber: this.app.supportContactNumber,
            };
        }
        return payload;
    }
    get defaultPayload() {
        return {
            welcomeMessageUuid: null,
            welcomeMessageContent: null,
            welcomeButtonTitle: null,
        };
    }
    get persistentMenuPayload() {
        const persistentMenu = this.app.shopNowPersistentMenu;
        if (this.app && persistentMenu) {
            return {
                shopNowPersistentMenuTitle: persistentMenu?.title,
                shopNowPersistentMenuUuid: persistentMenu?.uuid,
            };
        }
    }
    get welcomeMessagePayload() {
        if (this.welcomeMessage) {
            return {
                welcomeMessageUuid: this.welcomeMessage.uuid,
                welcomeMessageContent: this.welcomeMessage.content,
                welcomeButtonTitle: this.welcomeMessage.welcomeButtonTitle,
            };
        }
        return this.defaultPayload;
    }
    get greetingPayload() {
        const payload = { pageUuid: null, greeting: null };
        if (this.greeting) {
            return {
                pageUuid: this.pageUuid,
                greeting: this.greeting.default.text,
            };
        }
        return payload;
    }
    get storeTitlePayload() {
        const payload = { pageUuid: null, storeTitle: null };
        if (this.storeTitle) {
            return {
                pageUuid: this.pageUuid,
                storeTitle: this.storeTitle.storeTitle.text,
            };
        }
        return payload;
    }
    get isMallStoresManagement() {
        return this.$route.params.key === MALL_MANAGEMENT;
    }
    get formData() {
        // Remove first_time_opt_in notification for non-gcash app
        this.miniAppNotificationsByApp = this.miniAppNotificationsByApp.filter((notif) => {
            if (this.app.gcashAppId) {
                return true;
            }
            return notif.notificationType !== 'first_time_opt_in';
        });
        const miniAppNotifications = this.miniAppNotificationsByApp.map((detail) => {
            return new MiniAppNotificationInputModel(detail);
        });
        return {
            miniAppNotifications,
            ...this.appPayload,
            ...this.welcomeMessagePayload,
            ...this.storeTitlePayload,
            ...this.greetingPayload,
            ...this.persistentMenuPayload,
        };
    }
    async updateApp(form) {
        return this.$apollo.mutate({
            mutation: AppUpdate,
            variables: {
                input: {
                    appUuid: form.appUuid,
                    name: form.appName,
                    primaryCategories: Object.keys(form.categoryTree).filter((key) => form.categoryTree[key].primaryOption),
                    secondaryCategories: Object.keys(form.categoryTree)
                        .map((key) => form.categoryTree[key].secondaryOptions)
                        .flat(),
                    businessDescription: form.businessDescription,
                    emailNotifRecipients: form.emailNotifRecipients.join(','),
                    supportEmailList: form.supportEmailList.join(','),
                    supportContactNumber: form.supportContactNumber,
                    diy: this.diy,
                    referralCode: form.referralCode,
                    shoulderConvenienceFee: form.shoulderConvenienceFee,
                    shoulderProcessingFee: form.shoulderProcessingFee,
                    ageRequirement: form.ageRequirement,
                },
            },
        });
    }
    get notificationsQueryLoading() {
        return this.$apollo.queries.miniAppNotificationsByApp.loading;
    }
    async updateGreeting(form) {
        return this.$apollo.mutate({
            mutation: PageGreetingUpdate,
            variables: {
                input: {
                    pageUuid: form.pageUuid,
                    message: form.greeting,
                },
            },
        });
    }
    async updateStoreTitle(form) {
        return this.$apollo.mutate({
            mutation: PageStoreTitleUpdate,
            variables: {
                input: {
                    pageUuid: form.pageUuid,
                    message: form.storeTitle,
                },
            },
        });
    }
    async updateWelcomeMessage(form) {
        return this.$apollo.mutate({
            mutation: PageWelcomeMessageUpdate,
            variables: {
                input: {
                    cardUuid: form.welcomeMessageUuid,
                    content: form.welcomeMessageContent,
                    buttonLabel: form.welcomeButtonTitle,
                },
            },
        });
    }
    async updatePersistentMenu(form) {
        if (form.shopNowPersistentMenuUuid && form.shopNowPersistentMenuTitle) {
            return this.$apollo.mutate({
                mutation: PersistentMenuUpdate,
                variables: {
                    input: {
                        persistentMenuUuid: form.shopNowPersistentMenuUuid,
                        persistentMenuInput: form.shopNowPersistentMenuTitle,
                    },
                },
            });
        }
    }
    async updateWelcomeImage(form) {
        if (form.welcomeImage) {
            const inputImage = form.welcomeImage.map((input) => input.file);
            return this.$apollo.mutate({
                mutation: WelcomeImageUpdate,
                variables: {
                    input: {
                        appUuid: form.appUuid,
                        welcomeImage: inputImage,
                    },
                },
            });
        }
    }
    buildNotifParams(notifs) {
        return notifs.map((notif) => {
            const { content, uuid, active } = notif;
            const params = { notificationUuid: uuid, content, active };
            return params;
        });
    }
    async updateMiniAppNotifs(form) {
        await this.$apollo.mutate({
            mutation: MiniAppNotificationsUpdate,
            variables: {
                input: {
                    objectUuid: this.appUuid,
                    notifications: this.buildNotifParams(form.miniAppNotifications),
                },
            },
        });
        await this.$apollo.queries.miniAppNotificationsByApp?.refetch();
    }
};
__decorate([
    Prop({ type: String, required: true })
], PageSettingMixin.prototype, "appUuid", void 0);
__decorate([
    Prop({ type: String, required: true })
], PageSettingMixin.prototype, "pageUuid", void 0);
__decorate([
    Prop({ type: String })
], PageSettingMixin.prototype, "plan", void 0);
PageSettingMixin = __decorate([
    Component({
        apollo: {
            app: {
                query: AppBasic,
                variables() {
                    return {
                        appUuid: this.appUuid,
                    };
                },
                update(data) {
                    return new AppBasicModel(data.app);
                },
            },
            greeting: {
                query: PageGreeting,
                skip() {
                    return /notifications-manager/.test(this.$route.name);
                },
                variables() {
                    return {
                        pageUuid: this.pageUuid,
                    };
                },
                update(data) {
                    return new PageGreetingModel(data.pageSettingsGreeting);
                },
                fetchPolicy: 'network-only',
            },
            welcomeMessage: {
                query: PageWelcomeMessage,
                skip() {
                    return /notifications-manager/.test(this.$route.name);
                },
                variables() {
                    return {
                        appUuid: this.appUuid,
                    };
                },
                update(data) {
                    return new PageWelcomeMessageModel(data.welcomeMessage);
                },
                fetchPolicy: 'network-only',
            },
            miniAppNotificationsByApp: {
                query: MiniAppNotifications,
                variables() {
                    return {
                        appUuid: this.appUuid,
                    };
                },
                update(data) {
                    const miniAppNotifs = data.miniAppNotificationsByApp.edges;
                    const miniAppNotifsNodes = (notif) => {
                        return new MiniAppNotificationModel(notif.node);
                    };
                    return miniAppNotifs.map(miniAppNotifsNodes);
                },
            },
            storeTitle: {
                query: PageStoreTitle,
                variables() {
                    return {
                        pageUuid: this.pageUuid,
                    };
                },
                skip() {
                    return /notifications-manager/.test(this.$route.name);
                },
                update({ pageSettingsStoreTitle }) {
                    return new PageStoreTitleModel(pageSettingsStoreTitle);
                },
            },
        },
    })
], PageSettingMixin);
export default PageSettingMixin;
